import * as React from 'react';

import {
  getLocalTimeZone,
  now,
  CalendarDateTime,
  toCalendarDateTime,
  parseAbsoluteToLocal,
} from '@internationalized/date';
import { DateValue } from '@react-types/calendar';
import { DatePicker, TimeField } from '@rematter/ui-web';

import { useTranslations } from '../../../i18n/useTranslations';

export type ScheduleTestPayload = {
  scheduledTime: string;
};

type ScheduleTestFormProps = {
  value: ScheduleTestPayload;
  onChange: (value: ScheduleTestPayload) => void;
};

export const ScheduleTestForm: React.FC<ScheduleTestFormProps> = ({
  value,
  onChange,
}) => {
  const t = useTranslations();
  const timezone = getLocalTimeZone();

  const parsedDateTime = React.useMemo(() => {
    try {
      if (value.scheduledTime) {
        const zoned = parseAbsoluteToLocal(value.scheduledTime);
        return toCalendarDateTime(zoned);
      }
    } catch {
      // Fall back to current time if parse fails
    }
    const n = now(timezone);
    return toCalendarDateTime(n);
  }, [value.scheduledTime, timezone]);

  const handleDateChange = React.useCallback(
    (date: DateValue | null) => {
      if (!date) return;
      const newDateTime = new CalendarDateTime(
        date.year,
        date.month,
        date.day,
        parsedDateTime.hour,
        parsedDateTime.minute,
        parsedDateTime.second,
      );
      onChange({
        scheduledTime: newDateTime.toDate(timezone).toISOString(),
      });
    },
    [parsedDateTime, timezone, onChange],
  );

  const handleTimeChange = React.useCallback(
    (time: CalendarDateTime | null) => {
      if (!time) return;
      const newDateTime = new CalendarDateTime(
        parsedDateTime.year,
        parsedDateTime.month,
        parsedDateTime.day,
        time.hour,
        time.minute,
        time.second || 0,
      );
      onChange({
        scheduledTime: newDateTime.toDate(timezone).toISOString(),
      });
    },
    [parsedDateTime, timezone, onChange],
  );

  return (
    <div className="@ScheduleTestForm space-y-4">
      <p className="text-sm text-neutral/70">
        {t.dialogs.test.schedule.scheduledTimeDescription}
      </p>

      <div className="grid grid-cols-5 gap-2">
        <DatePicker
          label={t.dialogs.test.schedule.scheduledDate}
          value={parsedDateTime}
          onChange={handleDateChange}
          className="col-span-3"
        />
        <TimeField
          label={t.dialogs.test.schedule.time}
          value={parsedDateTime}
          onChange={handleTimeChange}
          className="col-span-2"
        />
      </div>
    </div>
  );
};

export const createDefaultScheduleTestPayload = (): ScheduleTestPayload => ({
  scheduledTime: new Date().toISOString(),
});
